import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { PantryItem } from "../lib/types";
import { ShoppingListModal } from "../components/ShoppingListModal";

export function Pantry() {
  const [items, setItems] = useState<PantryItem[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", quantityValue: 0, quantityUnit: "g", lowThreshold: 0, source: "" });

  async function load() {
    const res = await api.get<{ items: PantryItem[] }>("/pantry");
    setItems(res.items);
  }

  useEffect(() => {
    load();
  }, []);

  async function addStock(e: React.FormEvent) {
    e.preventDefault();
    await api.post("/pantry", form);
    setForm({ name: "", quantityValue: 0, quantityUnit: "g", lowThreshold: 0, source: "" });
    setAddOpen(false);
    load();
  }

  async function removeItem(id: string) {
    await api.delete(`/pantry/${id}`);
    load();
  }

  const lowCount = items.filter((i) => i.status !== "Fine").length;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h6 className="kicker">
            Pantry · {items.length} items · {lowCount} running out
          </h6>
          <h1 className="page-title">
            What you have
            <br />
            to work with
          </h1>
        </div>
        <div className="flex-row">
          <button className="btn btn-secondary" onClick={() => setListOpen(true)}>
            Build shopping list
          </button>
          <button className="btn btn-primary" onClick={() => setAddOpen((v) => !v)}>
            Add stock
          </button>
        </div>
      </div>

      <hr className="hr" style={{ margin: "30px 0 0" }} />

      {addOpen && (
        <form onSubmit={addStock} className="flex-row" style={{ margin: "20px 0", flexWrap: "wrap" }}>
          <input className="input" style={{ maxWidth: 240 }} placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input
            className="input tabular"
            style={{ maxWidth: 100 }}
            type="number"
            placeholder="Qty"
            value={form.quantityValue}
            onChange={(e) => setForm({ ...form, quantityValue: Number(e.target.value) })}
          />
          <select className="input" style={{ maxWidth: 90 }} value={form.quantityUnit} onChange={(e) => setForm({ ...form, quantityUnit: e.target.value })}>
            {["g", "kg", "ml", "L", "each"].map((u) => (
              <option key={u}>{u}</option>
            ))}
          </select>
          <input
            className="input tabular"
            style={{ maxWidth: 110 }}
            type="number"
            placeholder="Low at"
            value={form.lowThreshold}
            onChange={(e) => setForm({ ...form, lowThreshold: Number(e.target.value) })}
          />
          <input className="input" style={{ maxWidth: 160 }} placeholder="Source (optional)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
          <button className="btn btn-primary" type="submit">
            Save
          </button>
        </form>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 48, alignItems: "start" }}>
        <section>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: "34%" }}>Item</th>
                <th>On hand</th>
                <th>Bought</th>
                <th style={{ textAlign: "right" }}>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontSize: 14.5 }}>{p.name}</td>
                  <td className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 14 }}>
                    {p.quantityValue} {p.quantityUnit}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {p.purchasedAt ? new Date(p.purchasedAt).toLocaleDateString() : "—"}
                    {p.source ? ` · ${p.source}` : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <span className={`tag ${p.status === "Low" ? "tag-accent" : p.status === "Out" ? "tag-outline" : "tag-neutral"}`}>{p.status}</span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn btn-ghost" onClick={() => removeItem(p.id)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <p className="muted" style={{ padding: "24px 0" }}>
              Nothing on the shelf yet. Add stock above.
            </p>
          )}
        </section>

        <aside className="flex-col" style={{ gap: 28, paddingTop: 8 }}>
          <div style={{ borderTop: "2px solid var(--color-divider)", paddingTop: 18 }}>
            <h6 className="eyebrow" style={{ marginBottom: 12 }}>
              Running low or out
            </h6>
            {items
              .filter((i) => i.status !== "Fine")
              .map((i) => (
                <p key={i.id} className="muted" style={{ margin: "8px 0", fontSize: 13.5 }}>
                  <strong style={{ fontFamily: "var(--font-heading)", fontWeight: 800, color: "var(--color-text)" }}>{i.name}</strong> — {i.status.toLowerCase()}
                </p>
              ))}
            {items.filter((i) => i.status !== "Fine").length === 0 && <p className="muted-strong" style={{ fontSize: 13 }}>Everything's stocked.</p>}
          </div>
        </aside>
      </div>

      {listOpen && <ShoppingListModal onClose={() => setListOpen(false)} />}
    </main>
  );
}
